import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { MessageSquare, Send, CheckCircle, Loader2, AlertCircle, Bug, FileText, CreditCard, Sparkles, HelpCircle, User, Stethoscope, Users } from 'lucide-react';
import api, { formatApiError } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';

const CATEGORIES = [
  { value: 'bug', label: 'Bug / Something broken', icon: Bug, color: 'red' },
  { value: 'content', label: 'Content issue', icon: FileText, color: 'amber' },
  { value: 'billing', label: 'Billing / Payments', icon: CreditCard, color: 'violet' },
  { value: 'feature', label: 'Feature request', icon: Sparkles, color: 'sky' },
  { value: 'other', label: 'Other', icon: HelpCircle, color: 'slate' },
];

const ROLES = [
  { value: 'doctor', label: "I'm a Doctor", icon: Stethoscope },
  { value: 'patient', label: "I'm a Patient", icon: User },
  { value: 'visitor', label: 'Just exploring', icon: Users },
];

export default function ContactPage() {
  const { user } = useAuth();
  const [name, setName] = useState(user?.name || '');
  const [email, setEmail] = useState(user?.email || '');
  const [phone, setPhone] = useState('');
  const [role, setRole] = useState(user?.role === 'doctor' ? 'doctor' : user?.role === 'patient' ? 'patient' : 'visitor');
  const [category, setCategory] = useState('other');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || message.trim().length < 5) {
      setError('Please enter your name and a message (min 5 chars)');
      return;
    }
    setSubmitting(true); setError('');
    try {
      await api.post('/feedback/submit', {
        name: name.trim(),
        email: email.trim(),
        phone: phone.trim(),
        role, category,
        message: message.trim(),
        page_url: typeof window !== 'undefined' ? window.location.href : '',
      });
      setSent(true);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || 'Could not send — please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-sky-50/40" style={{ fontFamily: 'Manrope, sans-serif' }}>
      <Helmet>
        <title>Contact Us & Feedback — DocSite</title>
        <meta name="description" content="Share feedback, report a bug, or contact DocSite. Every message reaches our team directly." />
      </Helmet>

      <div className="max-w-3xl mx-auto px-6 md:px-12 py-14">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-sky-100 text-sky-700 text-xs font-semibold mb-4">
            <MessageSquare size={12} /> WE READ EVERY MESSAGE
          </div>
          <h1 className="text-3xl md:text-4xl tracking-tight font-medium text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="contact-title">
            Found a bug? Got an idea?<br />Tell us everything.
          </h1>
          <p className="text-base text-slate-600 mt-3 max-w-xl mx-auto">
            Feedback from doctors, patients, and curious visitors goes straight to our inbox — no ticket queues, no bots.
          </p>
        </div>

        {sent ? (
          <div className="bg-white rounded-3xl border border-emerald-200 p-10 text-center shadow-sm" data-testid="contact-success">
            <div className="w-14 h-14 rounded-full bg-emerald-100 mx-auto mb-4 flex items-center justify-center">
              <CheckCircle className="text-emerald-600" size={28} />
            </div>
            <h2 className="text-xl font-medium text-slate-900 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Message received.</h2>
            <p className="text-sm text-slate-600 max-w-md mx-auto">
              Thanks {name.split(' ')[0] || 'there'}. We usually respond within 24 hours — sooner for bugs and billing issues.
            </p>
            <button onClick={() => { setSent(false); setMessage(''); }} className="mt-6 text-sm text-sky-600 font-medium hover:text-sky-700" data-testid="contact-send-another">Send another →</button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="bg-white rounded-3xl border border-slate-200 p-6 md:p-10 shadow-sm space-y-7" data-testid="contact-form">
            {/* Role */}
            <div>
              <label className="text-xs font-semibold text-slate-700 mb-2 block uppercase tracking-wider">I am a...</label>
              <div className="grid grid-cols-3 gap-2">
                {ROLES.map(({ value, label, icon: Icon }) => (
                  <button type="button" key={value} onClick={() => setRole(value)}
                    className={`flex flex-col items-center gap-1.5 py-3 rounded-xl text-xs font-medium transition-all border ${role === value ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'}`}
                    data-testid={`contact-role-${value}`}>
                    <Icon size={16} /> {label}
                  </button>
                ))}
              </div>
            </div>

            {/* Name + email/phone */}
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-semibold text-slate-700 mb-2 block uppercase tracking-wider">Your name *</label>
                <input value={name} onChange={(e) => setName(e.target.value)} required maxLength={100}
                  className="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:border-sky-400 focus:ring-2 focus:ring-sky-100 outline-none text-sm"
                  data-testid="contact-name" />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-700 mb-2 block uppercase tracking-wider">Email (so we can reply)</label>
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} maxLength={200}
                  className="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:border-sky-400 focus:ring-2 focus:ring-sky-100 outline-none text-sm"
                  data-testid="contact-email" />
              </div>
            </div>

            <div>
              <label className="text-xs font-semibold text-slate-700 mb-2 block uppercase tracking-wider">Phone (optional)</label>
              <input value={phone} onChange={(e) => setPhone(e.target.value)} maxLength={30}
                className="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:border-sky-400 focus:ring-2 focus:ring-sky-100 outline-none text-sm"
                data-testid="contact-phone" />
            </div>

            {/* Category */}
            <div>
              <label className="text-xs font-semibold text-slate-700 mb-2 block uppercase tracking-wider">What's this about?</label>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                {CATEGORIES.map(({ value, label, icon: Icon }) => (
                  <button type="button" key={value} onClick={() => setCategory(value)}
                    className={`flex flex-col items-center gap-1 py-2.5 px-2 rounded-xl text-[11px] font-medium transition-all border ${category === value ? 'bg-sky-50 border-sky-300 text-sky-700' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'}`}
                    data-testid={`contact-category-${value}`}>
                    <Icon size={14} /> <span className="text-center leading-tight">{label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Message */}
            <div>
              <label className="text-xs font-semibold text-slate-700 mb-2 block uppercase tracking-wider">Your message *</label>
              <textarea value={message} onChange={(e) => setMessage(e.target.value)} required maxLength={4000} rows={6}
                placeholder="Describe what you saw, what you expected, and where on the site this happened..."
                className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-sky-400 focus:ring-2 focus:ring-sky-100 outline-none text-sm leading-relaxed"
                data-testid="contact-message" />
              <p className="text-[11px] text-slate-400 mt-1">{message.length}/4000</p>
            </div>

            {error && (
              <p className="text-sm text-red-600 flex items-center gap-1.5" data-testid="contact-error">
                <AlertCircle size={14} /> {error}
              </p>
            )}

            <button type="submit" disabled={submitting || !name.trim() || message.trim().length < 5}
              className="w-full md:w-auto flex items-center justify-center gap-2 px-8 py-3 rounded-full bg-sky-600 text-white font-medium hover:bg-sky-700 transition-colors disabled:opacity-50"
              data-testid="contact-submit">
              {submitting ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
              {submitting ? 'Sending...' : 'Send message'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
